import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * A card's own look (Huanrui, 2026-10-21): a small grey ring at the card's
 * bottom right opens a row of colours. The ring stays grey whatever is
 * chosen -- the card's edge is what shows the colour.
 *
 * Skill cards take a colour. Project cards take a colour or, from the last
 * circle (+), a picture of the person's own as the card's background.
 *
 * The ring is inside the card, so it leans and drifts with it; the colours
 * open in a layer of their own over the page (the card clips what spills out
 * of it), placed from where the ring is.
 */

/** The colours a card can take (the same nine as the Miro panel's cards). */
export const CARD_COLOURS: { name: string; hex: string }[] = [
  { name: 'Blue', hex: '#2f6fd6' },
  { name: 'Teal', hex: '#1a9aa8' },
  { name: 'Green', hex: '#2f9a5b' },
  { name: 'Yellow', hex: '#d4a80a' },
  { name: 'Orange', hex: '#f0892c' },
  { name: 'Red', hex: '#e25a44' },
  { name: 'Pink', hex: '#d94c8a' },
  { name: 'Purple', hex: '#7b4fd6' },
  { name: 'Graphite', hex: '#8a8a90' },
]

const HEX = /^#[0-9a-f]{6}$/i

export function isCardColour(value: unknown): value is string {
  return typeof value === 'string' && HEX.test(value)
}

/** '#2f6fd6' as the 'r, g, b' a tile's glow is given in. */
export function tripleOf(hex: string): string {
  const n = parseInt(hex.slice(1), 16)
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`
}

interface CardPaintProps {
  /** What the card is called, for the labels. */
  name: string
  colour: string | undefined
  onColour: (hex: string | null) => void
  /** Project cards: a picture as the background. Without this there is no + circle. */
  onPicture?: (file: File) => void
  /** The background picture now, to show in the + circle. */
  picture?: string
  /** Take the background picture away: held for three seconds on its circle. */
  onRemovePicture?: () => void
  /** A picture is going up. */
  busy?: boolean
  /** Where on the card: the skill card's ring sits in the corner, a project's beside the pin. */
  className?: string
}

/** How long the picture's circle is held to take the picture away. */
const HOLD_MS = 3000

export function CardPaint({
  name,
  colour,
  onColour,
  onPicture,
  onRemovePicture,
  picture,
  busy = false,
  className = '',
}: CardPaintProps) {
  /** Open, and where: the ring's right edge and top, in the window. */
  const [open, setOpen] = useState<{ right: number; bottom: number } | null>(null)
  const box = useRef<HTMLDivElement>(null)
  const menu = useRef<HTMLDivElement>(null)
  const chooser = useRef<HTMLInputElement>(null)
  /*
   * The picture's circle (2026-10-21): a click changes the picture; held for
   * three seconds, it takes the picture away. A ring fills round the circle
   * while it is held, so the wait is seen; letting go early is only a click.
   */
  const [holding, setHolding] = useState(false)
  const holdTimer = useRef<number | null>(null)
  const held = useRef(false)
  const stopHolding = () => {
    if (holdTimer.current !== null) window.clearTimeout(holdTimer.current)
    holdTimer.current = null
    setHolding(false)
  }
  useEffect(() => () => {
    if (holdTimer.current !== null) window.clearTimeout(holdTimer.current)
  }, [])

  useEffect(() => {
    if (!open) return
    const away = (event: PointerEvent) => {
      const target = event.target as Node
      if (!box.current?.contains(target) && !menu.current?.contains(target)) setOpen(null)
    }
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(null)
    }
    // It is placed once, from where the ring was: a scroll would leave it behind.
    const scrolled = () => setOpen(null)
    window.addEventListener('pointerdown', away)
    window.addEventListener('keydown', key)
    window.addEventListener('scroll', scrolled, true)
    window.addEventListener('resize', scrolled)
    return () => {
      window.removeEventListener('pointerdown', away)
      window.removeEventListener('keydown', key)
      window.removeEventListener('scroll', scrolled, true)
      window.removeEventListener('resize', scrolled)
    }
  }, [open])

  const plain = !colour

  return (
    <div
      ref={box}
      className={`card-paint${open ? ' card-paint--open' : ''}${busy ? ' card-paint--busy' : ''}${className ? ` ${className}` : ''}`}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className="card-paint__ring"
        title={busy ? 'Putting the picture up…' : 'Card colour'}
        aria-label={`Colour of ${name}`}
        aria-expanded={Boolean(open)}
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          const rect = event.currentTarget.getBoundingClientRect()
          setOpen((was) =>
            was ? null : { right: window.innerWidth - rect.right - 4, bottom: window.innerHeight - rect.top + 8 },
          )
        }}
      >
        <span className="card-paint__mark" aria-hidden="true" />
      </button>
      {open
        ? createPortal(
        <div
          ref={menu}
          className={`card-paint__menu${onPicture ? ' card-paint__menu--wide' : ''}`}
          role="menu"
          aria-label={`Colour of ${name}`}
          style={{ right: open.right, bottom: open.bottom }}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            role="menuitemradio"
            aria-checked={plain}
            className={`card-paint__swatch card-paint__swatch--none${plain ? ' card-paint__swatch--on' : ''}`}
            title="Default colour"
            aria-label="Default"
            onClick={() => {
              onColour(null)
              setOpen(null)
            }}
          />
          {CARD_COLOURS.map((one) => (
            <button
              key={one.hex}
              type="button"
              role="menuitemradio"
              aria-checked={colour === one.hex}
              className={`card-paint__swatch${colour === one.hex ? ' card-paint__swatch--on' : ''}`}
              style={{ background: one.hex }}
              title={one.name}
              aria-label={one.name}
              onClick={() => {
                onColour(one.hex)
                setOpen(null)
              }}
            />
          ))}
          {onPicture ? (
            <>
              <button
                type="button"
                className={`card-paint__swatch card-paint__swatch--add${picture ? ' card-paint__swatch--on' : ''}${
                  holding ? ' card-paint__swatch--holding' : ''
                }`}
                style={picture ? { backgroundImage: `url("${picture}")` } : undefined}
                title={
                  picture
                    ? 'Click to change the background picture · hold 3 seconds to remove it'
                    : 'A picture of your own as the background'
                }
                aria-label={picture ? 'Background picture: click to change, hold to remove' : 'Background picture'}
                disabled={busy}
                onPointerDown={(event) => {
                  event.stopPropagation()
                  held.current = false
                  if (!picture || !onRemovePicture || event.button !== 0) return
                  setHolding(true)
                  holdTimer.current = window.setTimeout(() => {
                    holdTimer.current = null
                    held.current = true
                    setHolding(false)
                    onRemovePicture()
                    setOpen(null)
                  }, HOLD_MS)
                }}
                onPointerUp={stopHolding}
                onPointerLeave={stopHolding}
                onPointerCancel={stopHolding}
                onContextMenu={(event) => event.preventDefault()}
                onClick={() => {
                  // The end of a hold that already took the picture away is not a click.
                  if (held.current) {
                    held.current = false
                    return
                  }
                  chooser.current?.click()
                }}
              >
                {picture ? null : <span aria-hidden="true">+</span>}
                {holding ? (
                  <svg className="card-paint__hold" viewBox="0 0 30 30" aria-hidden="true">
                    <circle cx="15" cy="15" r="13.5" pathLength="100" />
                  </svg>
                ) : null}
              </button>
              <input
                ref={chooser}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                hidden
                onChange={(event) => {
                  const file = event.target.files?.[0]
                  event.target.value = ''
                  if (file) {
                    onPicture(file)
                    setOpen(null)
                  }
                }}
              />
            </>
          ) : null}
        </div>,
        document.body,
      )
        : null}
    </div>
  )
}

/**
 * A picture cut to a card's shape (16:10, the cover crop, centred) at a size a
 * card needs, as JPEG: the background is drawn faint and small, and the copy
 * kept in the project stays light.
 */
export async function cardPicture(file: File): Promise<File> {
  const bitmap = await createImageBitmap(file)
  try {
    const W = 1280
    const H = 800
    const scale = Math.max(W / bitmap.width, H / bitmap.height)
    const sw = W / scale
    const sh = H / scale
    const sx = (bitmap.width - sw) / 2
    const sy = (bitmap.height - sh) / 2
    const canvas = document.createElement('canvas')
    canvas.width = W
    canvas.height = H
    const context = canvas.getContext('2d')
    if (!context) throw new Error('Could not draw the picture')
    context.fillStyle = '#111111'
    context.fillRect(0, 0, W, H)
    context.drawImage(bitmap, sx, sy, sw, sh, 0, 0, W, H)
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.86))
    if (!blob) throw new Error('Could not make the picture')
    const stem = file.name.replace(/\.[^.]+$/, '') || 'picture'
    return new File([blob], `Card background - ${stem}.jpg`, { type: 'image/jpeg' })
  } finally {
    bitmap.close()
  }
}
